import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { parseBarcode, type DeliveryNoteDetailDto, type DeliveryNoteSummaryDto, type DeliveryStockOptionDto, type DeliveryStockPositionDto } from "@hisense/shared";
import { apiFetch } from "@/lib/api";
import { formatQty } from "@/domain/money";
import { translateStatus } from "@/domain/i18n";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ScanField, lotScan, sameProduct, scanError } from "./ScanField";

type Source = { kind: "Stock"; position: DeliveryStockPositionDto } | { kind: "Direct"; note: string };
type Choice = { lineNo: number; position: DeliveryStockPositionDto };
const positionKey = (p: DeliveryStockPositionDto) => `${p.entryId}|${p.warehouseId}|${p.location}`;

/**
 * Xuất theo phiếu giao (0088 — xuất kho lúc hàng rời kho): quét số phiếu giao đang chờ giao → quét tem lô của hàng lấy cho
 * từng dòng (hệ tự khớp dòng theo mã, đơn vị, lô và số còn xuất bán được) → dòng kho không có hàng thì giao thẳng kèm ghi
 * chú → một nút "Xuất kho & chuyển Đang giao". Máy chủ kiểm lại toàn bộ và ghi sổ phiếu xuất bán trong cùng lần lưu.
 */
export function PickTask() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [dn, setDn] = useState<DeliveryNoteDetailDto | null>(null);
  const [options, setOptions] = useState<DeliveryStockOptionDto[]>([]);
  const [sources, setSources] = useState<Map<number, Source>>(new Map());
  const [choices, setChoices] = useState<Choice[]>([]);
  const [bulkNote, setBulkNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const lines = dn?.items.filter((it) => it.orderedQty > 0) ?? [];
  const positionsOf = (lineNo: number) => options.find((o) => o.lineNo === lineNo)?.positions ?? [];

  const openDn = async (raw: string) => {
    setError(null);
    setNotice(null);
    setDn(null);
    setSources(new Map());
    setChoices([]);
    setBusy(true);
    try {
      const list = await apiFetch<{ data: DeliveryNoteSummaryDto[] }>("/delivery-notes", { query: { q: raw, pageSize: 20 } });
      const hit = list.data.find((d) => d.dnNo.trim().toLowerCase() === raw.trim().toLowerCase());
      if (!hit) return setError(t("scan.pick.notFound", { no: raw }));
      if (hit.deliveryStatus !== "Pending") return setError(t("scan.pick.notPending", { no: hit.dnNo, status: translateStatus(t, hit.deliveryStatus) }));
      const [detail, stock] = await Promise.all([
        apiFetch<{ data: DeliveryNoteDetailDto }>(`/delivery-notes/${hit.id}`),
        apiFetch<{ data: DeliveryStockOptionDto[] }>(`/delivery-notes/${hit.id}/stock-options`),
      ]);
      setDn(detail.data);
      setOptions(stock.data);
      // Dòng kho không có hàng: chỉ có thể giao thẳng — đặt sẵn, chờ ghi chú.
      const initial = new Map<number, Source>();
      for (const it of detail.data.items) {
        if (it.orderedQty > 0 && !(stock.data.find((o) => o.lineNo === it.lineNo)?.positions.length)) initial.set(it.lineNo, { kind: "Direct", note: "" });
      }
      setSources(initial);
    } catch (e) {
      setError(scanError(e, t, raw));
    } finally {
      setBusy(false);
    }
  };

  const assign = (c: Choice) => {
    setChoices([]);
    setSources((prev) => new Map(prev).set(c.lineNo, { kind: "Stock", position: c.position }));
  };

  /**
   * Quét tem lô: tìm các dòng CHƯA có nguồn (dòng đã chọn giao thẳng cũng không bị quét đè) mà lô này khớp — đúng mã hàng
   * khi tem mang mã — và còn đủ số (trừ phần đã gán cho dòng khác). Mọi chỗ khớp là cùng một chỗ hàng (cùng lô, cùng vị trí,
   * cho các dòng giống nhau) thì gán dòng đầu; khác mã hàng hay khác vị trí thì máy không biết hàng vừa lấy là của dòng nào
   * — hỏi người quét, không đoán (đoán sai là trừ kho nhầm mã).
   */
  const scanLot = (raw: string) => {
    setError(null);
    setChoices([]);
    const hit = lotScan(raw, parseBarcode);
    if (!hit) return setError(t("scan.parseError", { code: raw }));
    const used = new Map<string, number>();
    for (const [lineNo, s] of sources) {
      if (s.kind !== "Stock") continue;
      const k = positionKey(s.position);
      used.set(k, (used.get(k) ?? 0) + (lines.find((l) => l.lineNo === lineNo)?.orderedQty ?? 0));
    }
    const found: Choice[] = [];
    for (const line of lines) {
      if (sources.has(line.lineNo) || !sameProduct(hit.productCode, line.itemNo ?? "")) continue;
      for (const p of positionsOf(line.lineNo)) {
        if (p.lotNumber.trim() === hit.lotNumber && p.available - (used.get(positionKey(p)) ?? 0) >= line.orderedQty) found.push({ lineNo: line.lineNo, position: p });
      }
    }
    if (!found.length) return setError(t("scan.pick.lotNoMatch", { lot: hit.lotNumber }));
    if (new Set(found.map((c) => positionKey(c.position))).size === 1) assign(found[0]!);
    else setChoices(found);
  };

  const setDirect = (lineNo: number, note: string) => setSources((prev) => new Map(prev).set(lineNo, { kind: "Direct", note }));
  const clear = (lineNo: number) => setSources((prev) => { const next = new Map(prev); next.delete(lineNo); return next; });
  const missing = lines.filter((l) => { const s = sources.get(l.lineNo); return !s || (s.kind === "Direct" && s.note.trim().length < 5); });
  const directLines = lines.filter((l) => sources.get(l.lineNo)?.kind === "Direct");

  const submit = async () => {
    if (!dn || busy || missing.length) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/delivery-notes/${dn.id}`, {
        method: "PUT",
        body: {
          expectedUpdatedAt: dn.updatedAt,
          deliveryStatus: "In Transit",
          items: lines.map((l) => {
            const s = sources.get(l.lineNo)!;
            return s.kind === "Stock"
              ? { lineNo: l.lineNo, deliveredQty: 0, fulfillment: "Stock", entryId: s.position.entryId, warehouseId: s.position.warehouseId, location: s.position.location }
              : { lineNo: l.lineNo, deliveredQty: 0, fulfillment: "Direct", fulfillmentNote: s.note.trim() };
          }),
        },
      });
      setNotice(t("scan.pick.done", { no: dn.dnNo }));
      setDn(null);
      setSources(new Map());
      setChoices([]);
      await Promise.all([qc.invalidateQueries({ queryKey: ["dns"] }), qc.invalidateQueries({ queryKey: ["dn", dn.id] }), qc.invalidateQueries({ queryKey: ["dashboard"] })]);
    } catch (e) {
      setError(scanError(e, t, ""));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <ScanField id="scan-pick-dn" label={t("scan.pick.scanDn")} placeholder={t("scan.pick.dnPlaceholder")} busy={busy} autoFocus={!dn} onScan={(v) => void openDn(v)} />
      {error && <p role="alert" className="text-sm text-danger">{error}</p>}
      {notice && <p role="status" className="rounded-sm bg-ok-tint px-3 py-2 text-sm text-ok">{notice}</p>}
      {dn && (
        <div className="space-y-3">
          <p className="text-sm"><span className="font-mono font-semibold">{dn.dnNo}</span> · {dn.billToName}</p>
          <p className="text-xs text-ink-soft">{t("scan.pick.scanHint")}</p>
          <ScanField id="scan-pick-lot" label={t("scan.scanLot")} placeholder={t("scan.scanLotPlaceholder")} busy={busy} onScan={scanLot} />
          {choices.length > 0 && (
            <fieldset className="min-w-0 space-y-2">
              <legend className="text-sm text-warn">{t("scan.pick.choose", { lot: choices[0]!.position.lotNumber })}</legend>
              {choices.map((c) => {
                const line = lines.find((l) => l.lineNo === c.lineNo);
                return (
                  <Button key={`${c.lineNo}|${positionKey(c.position)}`} variant="secondary" className="h-auto min-h-12 w-full justify-start whitespace-normal py-2 text-left" onClick={() => assign(c)}>
                    <span>
                      <span className="font-mono">{t("scan.pick.lineNo", { n: c.lineNo })} · {line?.itemNo}{line?.subRef ? ` / ${line.subRef}` : ""}</span>
                      {" · "}{c.position.warehouseName} <span className="font-mono">{c.position.location}</span>
                      {" · "}<span className="font-mono tabular">{formatQty(c.position.available)}</span> <span className="text-2xs text-ink-faint">{line?.unit}</span>
                    </span>
                  </Button>
                );
              })}
            </fieldset>
          )}
          {directLines.length >= 2 && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Input className="h-10 flex-1" aria-label={t("deliveryWorkspace.bulkDirectNote")} placeholder={t("deliveryWorkspace.bulkDirectNote")} value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} />
              <Button variant="secondary" disabled={bulkNote.trim().length < 5} onClick={() => setSources((prev) => {
                const next = new Map(prev);
                for (const l of directLines) { const s = next.get(l.lineNo); if (s?.kind === "Direct" && !s.note.trim()) next.set(l.lineNo, { kind: "Direct", note: bulkNote.trim() }); }
                return next;
              })}>{t("deliveryWorkspace.applyDirectNote", { count: directLines.filter((l) => { const s = sources.get(l.lineNo); return s?.kind === "Direct" && !s.note.trim(); }).length })}</Button>
            </div>
          )}
          <ul className="space-y-2">
            {lines.map((l) => {
              const s = sources.get(l.lineNo);
              const hasStock = positionsOf(l.lineNo).length > 0;
              return (
                <li key={l.id} className={`space-y-2 rounded-md border p-3 ${s && !(s.kind === "Direct" && s.note.trim().length < 5) ? "border-ok/40 bg-ok-tint/40" : "border-line bg-surface"}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-mono text-sm font-semibold">{l.itemNo}{l.subRef && <span className="text-ink-soft"> / {l.subRef}</span>}</p>
                    <p className="font-mono tabular text-sm">{formatQty(l.orderedQty)} <span className="text-2xs text-ink-faint">{l.unit}</span></p>
                  </div>
                  {l.description && <p className="text-xs text-ink-soft">{l.description}</p>}
                  {s?.kind === "Stock" ? (
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span>{t("scan.pick.fromLot", { lot: s.position.lotNumber, warehouse: s.position.warehouseName, location: s.position.location })}</span>
                      <Button size="sm" variant="ghost" onClick={() => clear(l.lineNo)}>{t("scan.reset")}</Button>
                    </div>
                  ) : s?.kind === "Direct" ? (
                    <div className="space-y-1">
                      <Input className="h-10" aria-label={`${t("scan.pick.directNote")} ${l.lineNo}`} placeholder={t("scan.pick.directNote")} value={s.note} onChange={(e) => setDirect(l.lineNo, e.target.value)} />
                      {hasStock ? (
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-2xs text-warn">{t("deliveryWorkspace.directWithStockWarning", { qty: formatQty(positionsOf(l.lineNo).reduce((sum, p) => sum + p.available, 0)) })}</p>
                          <Button size="sm" variant="ghost" onClick={() => clear(l.lineNo)}>{t("scan.reset")}</Button>
                        </div>
                      ) : <p className="text-2xs text-ink-faint">{t("scan.pick.noStock")}</p>}
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="text-warn">{t("scan.pick.needSource")}</span>
                      <Button size="sm" variant="secondary" onClick={() => setDirect(l.lineNo, "")}>{t("scan.pick.direct")}</Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {missing.length > 0 && <p className="text-sm text-ink-soft">{t("scan.pick.remaining", { n: missing.length })}</p>}
          <Button className="h-12 w-full" disabled={busy || missing.length > 0} onClick={() => void submit()}>{t("scan.pick.submit")}</Button>
        </div>
      )}
    </div>
  );
}
